import {
    DisconnectOutlined,
    LinkOutlined,
    PlusOutlined
} from "@ant-design/icons";
import { Button, Modal, Radio } from "antd";
import React, { useState } from "react";
import { SluitButton } from "~/components/buttons/SluitButton.jsx";
import { $t } from "~/i18n.js";
import { parse_timestring_to_timeobject } from "~/lib/localize.js";
import { OGNstartTijd } from "./Ogn.jsx";

// can be both to connect sleep to aerotow, or aerotow to sleep
export const SleepActions = ({
  flight,
  allFlights,
  sleepFlight,
  readOnly,
  disconnectSleepFlight,
  addNewSleepFlight,
  connectSleepFlight,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [connectFlightUUID, setConnectFlight] = useState(null);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [loadingAddNew, setLoadAddNew] = useState(false);

  if (readOnly) return null;

  // Determine which flights can be connected
  const dep_time = parse_timestring_to_timeobject(flight?.start_tijd);
  const slist = ["tmg", "sep", "tmg-a", "sep-a"];
  const method_filter =
    flight?.start_methode === "sleep"
      ? (m) => slist.includes(m)
      : (m) => !slist.includes(m);
  const feasibleFlightsForConnection =
    modalVisible &&
    allFlights &&
    allFlights
      .filter((f) => {
        // check all flights within 10 minutes of departure time
        const fTime = parse_timestring_to_timeobject(f.start_tijd);
        return (
          // Filter no itself, non-connected flights and those with matching methods
          f.uuid !== flight.uuid &&
          method_filter(f.start_methode) &&
          !f.sleep_uuid &&
          (!f.start_tijd ||
            !dep_time ||
            Math.abs(fTime.getTime() - dep_time.getTime()) <= 10 * 60 * 1000) // 10 minutes
        );
      })
      .map((f) => ({
        value: f.uuid,
        flight: f,
        label: (
          <p className="row" style={{ gap: 6 }}>
            <OGNstartTijd
              ogn_indicator={f.start_ogn}
              time={f.start_tijd}
              default_value={$t("Niet vertrokken")}
            />{" "}
            - {f.callsign} {f.registratie}{" "}
            <span className="gray">
              {f.type}
              {!f.registratie && $t("Geen vliegtuig geselecteerd")}
            </span>
            {f.gezagvoerder_naam && `- ${f.gezagvoerder_naam}`}
          </p>
        ),
      }));

  const disconnectSleep = async () => {
    setLoadingDisconnect(true);
    await disconnectSleepFlight();
    setLoadingDisconnect(false);
    setDisconnectModalVisible(false);
    setModalVisible(false);
  };

  // Trigger connecting a sleep flight
  const triggerConnectSleepFlight = async () => {
    setLoadingConnect(true);

    const sleepFlight = feasibleFlightsForConnection.find(
      (f) => f.value === connectFlightUUID
    );

    if (!sleepFlight) {
      setLoadingConnect(false);
      setModalVisible(false);
      return;
    }

    // set start_method for connected flight, which can only be tmg/sep or sleep in case that we reverse the connection
    let method = sleepFlight.flight.start_methode;
    if (["tmg", "tmg-a"].includes(method)) {
      method = "tmg-a";
    } else if (["sep", "sep-a"].includes(method)) {
      method = "sep-a";
    } else {
      method = "sleep";
    }

    await connectSleepFlight({
      sleep_uuid: connectFlightUUID,
      sleep_start_methode: method,
    });
    setLoadingConnect(false);
    setConnectFlight(null);
    setModalVisible(false);
  };

  const closeModal = () => {
    setConnectFlight(null);
    setLoadingDisconnect(false);
    setLoadingConnect(false);
    setModalVisible(false);
    setDisconnectModalVisible(false);
  };

  const triggerAddNewSleepFlight = async () => {
    setLoadAddNew(true);
    await addNewSleepFlight();
    setLoadAddNew(false);
  };

  return (
    <>
      {/* Connect Modal */}
      <Modal
        title={$t("Koppel de vlucht")}
        open={modalVisible}
        onCancel={closeModal}
        footer={[
          <SluitButton key="close" onClick={closeModal} />,
          <Button
            key="connect"
            type="primary"
            disabled={
              !feasibleFlightsForConnection ||
              feasibleFlightsForConnection.length === 0 ||
              !connectFlightUUID
            }
            loading={loadingConnect}
            onClick={triggerConnectSleepFlight}
            icon={<LinkOutlined />}
          >
            {$t("Koppel")}
          </Button>,
        ]}
      >
        <p>
          {$t("Huidige vlucht vertrek tijd")}:{" "}
          <span className="bold">{flight?.start_tijd || $t('Niet vertrokken')}</span>
        </p>

        <p className="subtitle" style={{ marginTop: 24 }}>
          {$t("Selecteer mogelijke sleepvlucht")}
        </p>
        {feasibleFlightsForConnection &&
        feasibleFlightsForConnection.length > 0 ? (
          <div>
            <Radio.Group
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
              onChange={(e) => setConnectFlight(e.target.value)}
              value={connectFlightUUID}
              options={feasibleFlightsForConnection}
            ></Radio.Group>
          </div>
        ) : (
          <p>{$t("Geen geschikte vluchten gevonden om te koppelen")}</p>
        )}
      </Modal>

      {/* Disconnect Modal */}
      <Modal
        title={$t("Sleep ontkoppelen")}
        open={disconnectModalVisible}
        onCancel={closeModal}
        footer={[
          <SluitButton key="close" onClick={closeModal} />,
          <Button
            key="disconnect"
            danger
            loading={loadingDisconnect}
            onClick={disconnectSleep}
            icon={<DisconnectOutlined />}
          >
            {$t("Ontkoppel")}
          </Button>,
        ]}
      >
        <p>
          {$t("Zeker weten?")} <br />
          {$t(
            "Wil je de vlucht wilt ontkoppelen (niet verwijderen) van de sleep"
          )}
        </p>
      </Modal>

      {!sleepFlight && (
        <div className="row" style={{ gap: 6, marginTop: 6 }}>
          <Button
            type="primary"
            size="small"
            loading={loadingConnect}
            disabled={!allFlights || !allFlights.length}
            onClick={() => setModalVisible(true)}
            icon={<LinkOutlined />}
          >
            {$t("Koppelen")}
          </Button>

          <Button
            size="small"
            loading={loadingAddNew}
            onClick={triggerAddNewSleepFlight}
            icon={<PlusOutlined />}
          >
            {$t("Toevoegen")}
          </Button>
        </div>
      )}

      {sleepFlight && (
        <Button
          danger
          icon={<DisconnectOutlined />}
          size="small"
          loading={loadingDisconnect}
          onClick={() => setDisconnectModalVisible(true)}
        >
          {$t("Ontkoppel")}
        </Button>
      )}
    </>
  );
};
